import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, EmptyState, Skeleton, Table, TBody, TD, TH, THead, TR } from '../components/ui'
import { PageHeader } from '../components/PageHeader'
import { usePurgeTrash, useRestoreTrash, useTrash } from '../api/hooks'
import { ApiError } from '../api/client'
import type { TrashItem } from '../api/types'

const KIND_LABELS: Record<string, string> = {
  person: 'Person',
  group: 'Group',
  occupancy: 'Occupancy',
  location: 'Location',
  door: 'Door',
  zone: 'Zone',
  accessgroup: 'Access group',
  accessgroupmember: 'Member',
  groupaccessgrant: 'Directory grant',
  memberrule: 'Member rule',
  doorpolicy: 'Door policy',
  schedule: 'Schedule',
  doorcode: 'Door code',
  doorcodeseries: 'Door code series',
  credential: 'Credential',
  system: 'System',
  systemintegration: 'Integration',
  managedsite: 'Site',
  mappingdismissal: 'Mapping dismissal',
  intercomcontact: 'Intercom contact',
  gateway: 'Gateway',
  edgeinstance: 'Edge instance',
  webhooksubscription: 'Webhook',
  commandlog: 'Command log',
  event: 'Event',
}

function kindLabel(kind: string): string {
  return KIND_LABELS[kind.toLowerCase()] ?? kind
}

function itemLink(item: TrashItem): string | null {
  const uid = item.unique_id
  if (uid.startsWith('op-')) return `/directory/${uid}`
  if (uid.startsWith('og-')) return `/groups/${uid}`
  if (uid.startsWith('ol-')) return `/locations/${uid}`
  if (uid.startsWith('odr-')) return `/doors/${uid}`
  if (uid.startsWith('oag-')) return `/access-groups/${uid}`
  if (uid.startsWith('osc-')) return `/schedules/${uid}`
  if (uid.startsWith('odc-')) return `/door-codes/${uid}`
  if (uid.startsWith('ot-')) return `/credentials/${uid}`
  if (uid.startsWith('os-')) return `/systems/${uid}`
  if (uid.startsWith('ogw-')) return `/gateways/${uid}`
  return null
}

function stamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`
}

export default function TrashPage() {
  const trash = useTrash()
  const restore = useRestoreTrash()
  const purge = usePurgeTrash()
  const [confirming, setConfirming] = useState<string | null>(null)
  const [action, setAction] = useState<'restore' | 'purge' | null>(null)

  const items = trash.data?.items ?? []
  const endpointMissing = trash.isError && trash.error instanceof ApiError && trash.error.status === 404

  function openConfirm(item: TrashItem, a: 'restore' | 'purge') {
    setConfirming(item.unique_id)
    setAction(a)
  }

  function closeConfirm() {
    setConfirming(null)
    setAction(null)
  }

  async function executeConfirm(uid: string) {
    if (action === 'restore') {
      await restore.mutateAsync(uid)
    } else if (action === 'purge') {
      await purge.mutateAsync(uid)
    }
    closeConfirm()
  }

  const confirmingItem = items.find((i) => i.unique_id === confirming)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Admin" title="Trash" />

      {endpointMissing && (
        <div className="rounded-sm border border-ostrel-warn bg-ostrel-warn/10 p-4 text-sm text-ostrel-ink">
          The trash endpoint is not available yet. Make sure the backend API is up to date.
        </div>
      )}

      {trash.isLoading && (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {!trash.isLoading && items.length === 0 && !endpointMissing && (
        <EmptyState title="Trash is empty" hint="Deleted rows appear here for 72 hours before they are purged automatically." />
      )}

      {items.length > 0 && (
        <div className="overflow-hidden rounded-sm border border-ostrel-line bg-ostrel-card">
          <Table>
            <THead>
              <TR>
                <TH>Kind</TH>
                <TH>Name</TH>
                <TH>Deleted at</TH>
                <TH>Deleted by</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((item) => {
                const link = itemLink(item)
                return (
                  <TR key={item.unique_id}>
                    <TD>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-ostrel-ink-muted">
                        {kindLabel(item.kind)}
                      </span>
                    </TD>
                    <TD>
                      {link ? (
                        <Link to={link} className="text-ostrel-ink hover:text-ostrel-gold hover:underline">
                          {item.name}
                        </Link>
                      ) : (
                        <span className="text-ostrel-ink">{item.name}</span>
                      )}
                      <span className="ml-2 text-xs text-ostrel-ink-muted">{item.unique_id}</span>
                    </TD>
                    <TD className="tnum text-xs text-ostrel-ink-muted">{stamp(item.deleted_at)}</TD>
                    <TD className="text-xs text-ostrel-ink-muted">{item.deleted_by || '—'}</TD>
                    <TD className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openConfirm(item, 'restore')}
                          disabled={restore.isPending || purge.isPending}
                        >
                          Restore
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => openConfirm(item, 'purge')}
                          disabled={restore.isPending || purge.isPending}
                        >
                          Purge
                        </Button>
                      </div>
                    </TD>
                  </TR>
                )
              })}
            </TBody>
          </Table>
        </div>
      )}

      {confirmingItem && action && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ostrel-navy/60 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-sm border border-ostrel-line bg-ostrel-card p-6 shadow-lg">
            <h2 className="font-display text-lg text-ostrel-ink">
              {action === 'restore' ? 'Restore deleted row' : 'Permanently delete row'}
            </h2>
            <p className="mt-2 text-sm text-ostrel-ink-muted">
              {action === 'restore'
                ? `Restore ${kindLabel(confirmingItem.kind).toLowerCase()} “${confirmingItem.name}”. Vendor bindings are not restored — re-adopt if needed.`
                : `Permanently purge ${kindLabel(confirmingItem.kind).toLowerCase()} “${confirmingItem.name}”. This cannot be undone.`}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="ghost" onClick={closeConfirm}>
                Cancel
              </Button>
              <Button
                variant={action === 'purge' ? 'danger' : 'primary'}
                onClick={() => executeConfirm(confirmingItem.unique_id)}
                disabled={restore.isPending || purge.isPending}
              >
                {action === 'restore' ? 'Restore' : 'Purge'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
